import {
  FiLink,
  FiPieChart,
  FiZap,
  FiEdit3,
  FiShield,
  FiLayers,
  FiCheckCircle,
  FiTrendingUp,
  FiGlobe,
  FiClock,
} from "react-icons/fi";

export const featurePages = {
  "brand-ready-short-links": {
    slug: "brand-ready-short-links",
    eyebrow: "Feature · Link Branding",
    title: "Brand-ready short links that build trust instantly",
    description:
      "Create memorable links with consistent slugs, campaign naming clarity, and share-ready formatting that reflects your brand everywhere.",
    icon: FiLink,
    heroStats: [
      { label: "Brand consistency", value: "High" },
      { label: "Setup time", value: "< 60 sec" },
      { label: "Campaign clarity", value: "Structured" },
    ],
    capabilities: [
      {
        title: "Custom slug control",
        detail:
          "Use readable, campaign-aligned slugs that improve recognition and confidence before every click.",
      },
      {
        title: "Naming conventions at scale",
        detail:
          "Keep links organized by campaign, team, or region so reporting stays clean and reproducible.",
      },
      {
        title: "Reusable branded format",
        detail:
          "Standardize links across social, email, and paid channels without reinventing process each launch.",
      },
    ],
    outcomes: [
      "Higher trust in shared links",
      "Stronger brand recall in campaigns",
      "Cleaner collaboration between teams",
    ],
    useCases: [
      {
        heading: "Product launches",
        text: "Ship links with clear product and campaign naming to keep cross-team distribution aligned.",
      },
      {
        heading: "Creator partnerships",
        text: "Provide creators with polished URLs that look credible and fit your messaging style.",
      },
      {
        heading: "Multi-channel rollouts",
        text: "Maintain a uniform link language across ads, newsletters, and social media assets.",
      },
    ],
    screenshot: {
      width: 960,
      height: 720,
      label: "Add a brand-link editor screenshot here",
    },
  },
  "live-performance-analytics": {
    slug: "live-performance-analytics",
    eyebrow: "Feature · Analytics",
    title: "Live performance analytics that guide smarter decisions",
    description:
      "Track traffic and engagement as campaigns unfold, with actionable visibility into source, geography, browser, and device behavior.",
    icon: FiPieChart,
    heroStats: [
      { label: "Insight speed", value: "Real-time" },
      { label: "Dimensions", value: "Source + Geo + Device" },
      { label: "Decision loop", value: "Faster" },
    ],
    capabilities: [
      {
        title: "Source-level breakdowns",
        detail:
          "Understand which channels are driving qualified traffic and where to increase spend.",
      },
      {
        title: "Geographic intelligence",
        detail:
          "View country and regional performance to adapt creative, targeting, or localization quickly.",
      },
      {
        title: "Device and browser insights",
        detail:
          "Identify technical and UX patterns that influence conversion behavior across platforms.",
      },
    ],
    outcomes: [
      "Better budget allocation by channel",
      "Faster campaign iteration cycles",
      "More confident reporting to stakeholders",
    ],
    useCases: [
      {
        heading: "Paid media optimization",
        text: "Shift spend based on live performance signals instead of waiting for delayed reports.",
      },
      {
        heading: "Growth experiments",
        text: "Measure variant performance quickly and double down on winning distribution paths.",
      },
      {
        heading: "Weekly leadership reporting",
        text: "Turn raw click activity into clear narratives on what is growing and why.",
      },
    ],
    screenshot: {
      width: 960,
      height: 720,
      label: "Add an analytics dashboard screenshot here",
    },
  },
  "fast-global-redirects": {
    slug: "fast-global-redirects",
    eyebrow: "Feature · Performance",
    title: "Fast global redirects designed for campaign spikes",
    description:
      "Deliver low-latency redirects worldwide so every click feels instant, even during peak launch windows and viral traffic bursts.",
    icon: FiZap,
    heroStats: [
      { label: "Latency target", value: "< 60 ms" },
      { label: "Uptime", value: "99.99%" },
      { label: "Burst readiness", value: "High" },
    ],
    capabilities: [
      {
        title: "Low-latency route handling",
        detail:
          "Ensure users reach destination pages quickly with minimal delay from click to load.",
      },
      {
        title: "Traffic surge stability",
        detail:
          "Handle launch-day spikes without degrading redirect reliability or campaign continuity.",
      },
      {
        title: "Operational confidence",
        detail:
          "Run links in production environments where consistency and responsiveness directly impact results.",
      },
    ],
    outcomes: [
      "Lower drop-off from slow redirects",
      "Smoother high-volume campaign launches",
      "More dependable customer journeys",
    ],
    useCases: [
      {
        heading: "Flash promotions",
        text: "Capture attention windows with fast redirects that keep momentum from ad click to destination.",
      },
      {
        heading: "Global launches",
        text: "Provide a consistent link experience for audiences across regions and time zones.",
      },
      {
        heading: "Viral social moments",
        text: "Stay stable when traffic surges unexpectedly from creator posts or trending content.",
      },
    ],
    screenshot: {
      width: 960,
      height: 720,
      label: "Add a redirect performance screenshot here",
    },
  },
  "edit-links-anytime": {
    slug: "edit-links-anytime",
    eyebrow: "Feature · Flexibility",
    title: "Edit links anytime without breaking published URLs",
    description:
      "Update destinations on the fly so live campaigns remain active while your teams adapt messaging, targeting, and offer strategy.",
    icon: FiEdit3,
    heroStats: [
      { label: "URL stability", value: "Persistent" },
      { label: "Update speed", value: "Instant" },
      { label: "Operational risk", value: "Reduced" },
    ],
    capabilities: [
      {
        title: "Destination hot-swaps",
        detail:
          "Change landing destinations in seconds while keeping the same public short URL.",
      },
      {
        title: "No-republish workflow",
        detail:
          "Avoid updating every external placement when campaign pages or offers evolve.",
      },
      {
        title: "Experiment-friendly routing",
        detail:
          "Support ongoing optimization by redirecting to refreshed pages or conversion variants.",
      },
    ],
    outcomes: [
      "Less campaign downtime",
      "Faster adaptation during live runs",
      "Lower coordination overhead",
    ],
    useCases: [
      {
        heading: "Limited-time offers",
        text: "Swap destination pages as promotions shift without touching previously distributed links.",
      },
      {
        heading: "Creative refresh cycles",
        text: "Keep the same link while testing new pages, messaging, and conversion flows.",
      },
      {
        heading: "Operational incident response",
        text: "Redirect to safe fallback pages immediately when destination issues are detected.",
      },
    ],
    screenshot: {
      width: 960,
      height: 720,
      label: "Add a live link editing screenshot here",
    },
  },
  "expiration-and-controls": {
    slug: "expiration-and-controls",
    eyebrow: "Feature · Governance",
    title: "Expiration and controls for safer campaign operations",
    description:
      "Protect users and campaigns with time-based expiry and operational controls that keep links accurate, relevant, and policy-compliant.",
    icon: FiShield,
    heroStats: [
      { label: "Control model", value: "Policy-driven" },
      { label: "Expiry support", value: "Built-in" },
      { label: "Security posture", value: "Improved" },
    ],
    capabilities: [
      {
        title: "Time-based expiry",
        detail:
          "Automatically disable or retire campaign links after specific windows.",
      },
      {
        title: "Usage governance",
        detail:
          "Apply predictable controls that reduce accidental misuse across teams.",
      },
      {
        title: "Lifecycle clarity",
        detail:
          "Manage active, scheduled, and expired links with clear operational visibility.",
      },
    ],
    outcomes: [
      "Cleaner campaign hygiene",
      "Reduced risk from stale links",
      "Better compliance for time-sensitive promotions",
    ],
    useCases: [
      {
        heading: "Event campaigns",
        text: "Expire registration and promo links automatically once event windows close.",
      },
      {
        heading: "Regulated promotions",
        text: "Ensure offers are only accessible during approved compliance periods.",
      },
      {
        heading: "Partnership collaborations",
        text: "Apply clear link lifecycles for co-marketing initiatives and affiliate campaigns.",
      },
    ],
    screenshot: {
      width: 960,
      height: 720,
      label: "Add an expiration settings screenshot here",
    },
  },
  "qr-and-channel-ops": {
    slug: "qr-and-channel-ops",
    eyebrow: "Feature · Distribution",
    title: "QR and channel operations for online and offline reach",
    description:
      "Generate ready-to-share QR assets and track performance by channel to understand how every touchpoint contributes to growth.",
    icon: FiLayers,
    heroStats: [
      { label: "Asset format", value: "QR-ready" },
      { label: "Channel visibility", value: "Clear" },
      { label: "Activation speed", value: "Immediate" },
    ],
    capabilities: [
      {
        title: "Instant QR generation",
        detail:
          "Produce QR assets for print, retail, events, and packaging without extra tooling.",
      },
      {
        title: "Channel segmentation",
        detail:
          "Break down performance by campaign source to identify where your audience converts best.",
      },
      {
        title: "Cross-channel continuity",
        detail:
          "Connect online and offline activity to one reporting workflow and shared destination logic.",
      },
    ],
    outcomes: [
      "Greater visibility across touchpoints",
      "More measurable offline campaigns",
      "Improved campaign coordination",
    ],
    useCases: [
      {
        heading: "Retail packaging",
        text: "Turn product packaging into trackable entry points for onboarding and offers.",
      },
      {
        heading: "Events and booths",
        text: "Deploy QR-based call-to-actions and monitor engagement by location or event segment.",
      },
      {
        heading: "Omnichannel launches",
        text: "Measure how digital and physical channels contribute to one campaign objective.",
      },
    ],
    screenshot: {
      width: 960,
      height: 720,
      label: "Add a QR and channel analytics screenshot here",
    },
  },
};

export const featureHighlights = [
  { icon: FiCheckCircle, text: "Production-ready workflows" },
  { icon: FiTrendingUp, text: "Designed for growth teams" },
  { icon: FiGlobe, text: "Built for global distribution" },
  { icon: FiClock, text: "Fast setup and iteration" },
];
